import React, { useState } from "react";
import { Badge, Modal, EmptyState as Empty, Pagination } from "@/components/ui";
import { fmt, fmtDate } from "@/lib/formatters";
import { useGeneralExpensesLogic } from "../hooks/useGeneralExpensesLogic";
import {
  Plus,
  Search,
  Filter,
  ChevronDown,
  TrendingDown,
  Calendar,
  LayoutGrid,
  List,
  Wallet,
  Receipt,
  Download,
  Eye,
  Edit2,
  UserX
} from "lucide-react";
import { AddGeneralExpenseModal } from "./AddGeneralExpenseModal.jsx";
import { ViewGeneralExpenseModal } from "./ViewGeneralExpenseModal.jsx";
import { DisburseExpenseModal } from "./DisburseExpenseModal.jsx";
import { ProjectGeneralControlPanel } from "./project_general_control_pannel.jsx";
import { ExpenseGrid } from "./ExpenseGrid.jsx";
import { ProjectGeneralStats } from "./project_general_stats.jsx";
import { ProjectGeneralTable } from "./project_general_table.jsx";

const PM = {
  cash: { l: "Cash", icon: "💵" },
  bank: { l: "Bank Transfer", icon: "🏦" },
  upi: { l: "UPI", icon: "📱" },
  cheque: { l: "Cheque", icon: "📄" }
};

export const GeneralExpensesPage = ({ initialCategory = "all", TabsComponent }) => {
  const {
    search, setSearch,
    filters, setFilters,
    sortBy, setSortBy,
    view, setView,
    showStats, setShowStats,
    page, setPage, totalPages,
    expenses, employees, projects,
    stats,
    user,
    handleAddExpense, handleEditExpense, handleToggleDisable, handleDisburseExpense,
    canManageExpenses, canDisburseExpenses
  } = useGeneralExpensesLogic(initialCategory);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedExpenseId, setSelectedExpenseId] = useState(null);
  const selectedExpense = expenses.find(e => e._id === selectedExpenseId);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDisburseModalOpen, setIsDisburseModalOpen] = useState(false);
  const [selectedDisburseExpenseId, setSelectedDisburseExpenseId] = useState(null);
  const selectedDisburseExpense = expenses.find(e => e._id === selectedDisburseExpenseId);

  const CATEGORIES = [
    { key: "general", label: "General Expenses" },
    { key: "project", label: "Project Expenses" }
  ];

  const [isExporting, setIsExporting] = useState(false);
  const handleExport = async () => {
    if (isExporting || !expenses || expenses.length === 0) return;
    try {
      setIsExporting(true);
      const { exportToExcel } = await import("@/utils/exportUtils");
      const exportData = expenses.map(e => ({
        "Expense ID": e.customId || e._id?.toString().slice(-6).toUpperCase(),
        "Date": fmtDate(e.date || e.createdAt),
        "Category": e.category?.name || e.category || "—",
        "Sub Category": e.subCategory?.name || e.subCategory || "—",
        "Amount (₹)": e.amount || 0,
        "Status": e.status?.toUpperCase() || "PENDING",
        "Type": e.relatedToProject ? "Project" : "General",
        "Project": e.project?.projectName || e.project?.customerName || "—",
        "Paid To": e.paidTo?.name || e.paidToName || e.paidTo || "—",
        "Payment Mode": e.paymentMode || e.paymentMethod || "—",
        "Requested By": e.requestedBy?.name || "—",
        "Approved By": e.approvedBy?.name || "—",
        "Disbursed Date": e.disbursedDate ? fmtDate(e.disbursedDate) : "—",
        "Remarks": e.remarks || e.description || "—"
      }));
      await exportToExcel(exportData, `Expenses_Export_${new Date().toISOString().split('T')[0]}`);
    } catch (err) {
      console.error("Failed to export expenses:", err);
    } finally {
      setIsExporting(false);
    }
  };

  /* Developer : Nithin
  Date : 26-06-2026
  changes: changed space-y-6 to match the AccountsScreen tab gaps (gap-5) for uniform layout spacing */
  return (
    <div className="flex flex-col gap-5 w-full min-w-0 max-w-full">
      {/* Stats Section */}
      <ProjectGeneralStats 
        stats={stats} 
        isExpanded={showStats} 
        onToggle={() => setShowStats(!showStats)} 
      />

      <div className="flex flex-col gap-2">
        {TabsComponent && <TabsComponent />}

        {/* Control Panel */}
        <ProjectGeneralControlPanel
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          sortBy={sortBy}
          setSortBy={setSortBy}
          view={view}
          setView={setView}
          onAddClick={() => setIsAddModalOpen(true)}
          categories={CATEGORIES}
          showStats={showStats}
          setShowStats={setShowStats}
          addLabel={filters.category === 'project' ? 'Project Expense' : (filters.category === 'general' ? 'General Expense' : 'Add Expense')}
          onExport={handleExport}
          isExporting={isExporting}
        />
      </div>

      {/* Content View */}
      {view === "kanban" ? (
        <ExpenseGrid
          expenses={expenses}
          onView={(e) => { setSelectedExpenseId(e._id); setIsViewModalOpen(true); }}
          onEdit={(e) => { setEditData(e); setIsEditModalOpen(true); }}
          onToggleDisable={handleToggleDisable}
          onDisburse={(e) => { setSelectedDisburseExpenseId(e._id); setIsDisburseModalOpen(true); }}
          canDisburseExpenses={canDisburseExpenses}
        />
      ) : (
        <ProjectGeneralTable 
          expenses={expenses}
          onView={(e) => { setSelectedExpenseId(e._id); setIsViewModalOpen(true); }}
          onEdit={(e) => { setEditData(e); setIsEditModalOpen(true); }}
          onToggleDisable={handleToggleDisable}
          onDisburse={(e) => { setSelectedDisburseExpenseId(e._id); setIsDisburseModalOpen(true); }}
          canDisburseExpenses={canDisburseExpenses}
          hideProjectColumns={initialCategory === 'general'}
        />
      )}

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <div className="mt-4 px-1">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      )}

      {/* Add/Edit Modal */}
      <AddGeneralExpenseModal
        isOpen={isAddModalOpen || isEditModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setIsEditModalOpen(false);
          setEditData(null);
        }}
        onConfirm={(data) => {
          if (isEditModalOpen) return handleEditExpense(editData._id, data);
          return handleAddExpense(data);
        }}
        mode={isEditModalOpen ? "edit" : "add"}
        editData={editData}
        employees={employees}
        projects={projects}
        user={user}
        customTitle={
          isEditModalOpen
            ? (editData?.relatedToProject ? "Edit Project Expense" : "Edit General Expense")
            : (filters.category === "project" ? "Request Project Expense" : "Request General Expense")
        }
      />

      <ViewGeneralExpenseModal
        isOpen={isViewModalOpen}
        onClose={() => { setIsViewModalOpen(false); setSelectedExpenseId(null); }}
        expense={selectedExpense}
        projects={projects}
      />

      <DisburseExpenseModal
        isOpen={isDisburseModalOpen}
        onClose={() => setIsDisburseModalOpen(false)}
        expense={selectedDisburseExpense}
        onSubmit={handleDisburseExpense}
      />
    </div>
  );
};
